
import { Component, ChangeDetectionStrategy, inject, signal, OnInit, OnDestroy, computed } from '@angular/core';
import { CommonModule, DatePipe, DOCUMENT } from '@angular/common';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { DataService } from '../../services/data.service';
import { PrescriptionService } from '../../services/prescription.service'; 
import { AuthService } from '../../services/auth.service';
import { Prescription } from '../../models/index';
import { SupabaseClient } from '@supabase/supabase-js';
import { getSupabase } from '../../firebase';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { PrescriptionFormComponent } from '../prescription-form/prescription-form.component';
import { MedicalCertificateComponent } from '../medical-certificate/medical-certificate.component'; // Import Medical Certificate
import { NotificationService } from '../../services/notification.service';

declare var html2canvas: any;

type LoadingState = 'loading' | 'loaded' | 'error';
type PreviewMode = 'none' | 'prescription' | 'certificate'; // Define modes

@Component({
  selector: 'app-pharmacy',
  standalone: true,
  templateUrl: './pharmacy.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslatePipe, DatePipe, ReactiveFormsModule, PrescriptionFormComponent, MedicalCertificateComponent, FormsModule]
})
export class PharmacyComponent implements OnInit, OnDestroy {
  private dataService = inject(DataService);
  private prescriptionService = inject(PrescriptionService);
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);
  private supabase: SupabaseClient | null = getSupabase();
  private fb: FormBuilder = inject(FormBuilder);
  private document: Document = inject(DOCUMENT);

  // State
  approvedPrescriptions = signal<Prescription[]>([]);
  selectedPrescription = signal<Prescription | null>(null);
  loadingState = signal<LoadingState>('loading');
  
  // Search State
  queueSearchTerm = signal<string>('');

  // Computed Filtered Queue
  filteredQueue = computed(() => {
    const term = this.queueSearchTerm().toLowerCase().trim();
    const list = this.approvedPrescriptions();
    if (!term) return list;
    return list.filter(p => 
      p.customerName?.toLowerCase().includes(term) || 
      p.customerCitizenId?.includes(term)
    );
  });
  
  // Preview & Printing State
  previewMode = signal<PreviewMode>('none'); // Changed from boolean to enum
  isGeneratingImage = signal(false);
  
  // Dispense Modal State
  isDispenseModalOpen = signal(false);
  isProcessing = signal(false);
  dispenseForm: FormGroup;

  private subscription: any;

  constructor() {
      this.dispenseForm = this.fb.group({
          purpose: ['TREATMENT', Validators.required], 
          quantity: [0, [Validators.required, Validators.min(0.1)]],
          unit: ['GRAM', Validators.required],
          lotNumber: [''],
          notes: ['']
      });
  }

  ngOnInit(): void {
    this.loadQueue();
    this.setupRealtime();
  }

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
  }

  setupRealtime() {
    if (!this.supabase) return;
    this.subscription = this.supabase.channel('pharmacy_queue')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'prescriptions' }, (payload) => {
         if (payload.new['status'] === 'APPROVED') {
             this.notificationService.playSuccessSound(); 
             this.loadQueue(); 
         }
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'prescriptions' }, (payload) => {
         if (payload.new['status'] === 'APPROVED') {
             this.notificationService.playSuccessSound();
             this.loadQueue();
         }
      })
      .subscribe();
  }

  async loadQueue(): Promise<void> {
    this.loadingState.set('loading');
    try {
      // *** THE FIX: Call the correct function for the pharmacy queue ***
      const data = await this.dataService.getApprovedPrescriptions();
      
      this.approvedPrescriptions.set(data);
      this.loadingState.set('loaded');

    } catch (e) {
      console.error(e);
      this.loadingState.set('error');
    }
  }

  selectItem(p: Prescription) {
      this.selectedPrescription.set(p);
  }

  // --- PREVIEW LOGIC UPDATED ---

  enterPrescriptionPreview(p: Prescription) {
      this.selectedPrescription.set(p);
      this.previewMode.set('prescription');
  }

  enterCertificatePreview(p: Prescription) {
      this.selectedPrescription.set(p);
      this.previewMode.set('certificate');
  }

  closePreview() {
      this.previewMode.set('none');
  }

  // Old smart print for Prescription Form (Legacy Logic preserved)
  async printSmartly(): Promise<void> {
    const paperElement = this.document.querySelector('.A4-paper');
    if (!paperElement) {
        this.notificationService.showWarning('Document not ready. Please wait a moment.');
        return;
    }
    this.isGeneratingImage.set(true);
    try {
        const styles = Array.from(this.document.querySelectorAll('style, link[rel="stylesheet"]'))
          .map(node => node.outerHTML)
          .join('');
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.visibility = 'hidden';
        document.body.appendChild(iframe);
        const doc = iframe.contentWindow?.document;
        if (!doc) throw new Error("Iframe document not created");
        doc.open();
        doc.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Prescription</title>
                ${styles}
                <style>
                    body { margin: 0; padding: 0; background: white; font-family: 'Sarabun', sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    @page { size: A4 portrait; margin: 0; }
                    .A4-paper { width: 210mm; height: 297mm; padding: 2cm 2cm 2cm 2.5cm; margin: 0 auto; position: relative; box-sizing: border-box; }
                </style>
            </head>
            <body>
                ${paperElement.outerHTML}
                <script>window.onload = function() { setTimeout(function() { window.print(); }, 500); };</script>
            </body>
            </html>
        `);
        doc.close();
        setTimeout(() => {
            document.body.removeChild(iframe);
            this.isGeneratingImage.set(false);
        }, 3000);
    } catch (err) {
        console.error("Smart Print Error:", err);
        this.isGeneratingImage.set(false);
    }
  }

  // Old download image for Prescription Form (Legacy Logic preserved)
  async downloadImage() {
    if (typeof html2canvas === 'undefined') {
      this.notificationService.showWarning('Image library missing.');
      return;
    }
    const element = this.document.querySelector('.A4-paper') as HTMLElement;
    if (!element) return;
    this.isGeneratingImage.set(true);
    try {
      const canvas = await html2canvas(element, { scale: 3, useCORS: true, allowTaint: true, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      const pres = this.selectedPrescription();
      const patientName = pres?.customerName?.replace(/\s/g, '_') || 'Patient';
      link.download = `Prescription_${patientName}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error(err);
    } finally {
      this.isGeneratingImage.set(false);
    }
  }

  // --- DISPENSE LOGIC ---

  openDispenseModal(p: Prescription) {
      this.selectedPrescription.set(p);
      if (p.approvedItems) {
          this.dispenseForm.patchValue({
              purpose: 'TREATMENT',
              quantity: p.approvedItems.totalAmount,
              unit: p.approvedItems.dosageUnit,
              lotNumber: ''
          });
      }
      this.isDispenseModalOpen.set(true);
  }

  closeDispenseModal() {
      this.isDispenseModalOpen.set(false);
  }

  async confirmDispense() {
      if (this.dispenseForm.invalid || !this.selectedPrescription()) return;
      this.isProcessing.set(true);
      try {
          const dispensedData = this.dispenseForm.value;
          dispensedData.productName = "Medical Cannabis"; 
          await this.prescriptionService.recordDispensing(this.selectedPrescription()!.id, dispensedData);
          this.closeDispenseModal();
          this.selectedPrescription.set(null);
          await this.loadQueue();
          this.notificationService.showSuccess('บันทึกการจ่ายยาเรียบร้อยแล้ว');
      } catch(e: any) {
          this.notificationService.handleError(e);
      } finally {
          this.isProcessing.set(false);
      }
  }
}
